import { Eye, EyeOff } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/cn";
import { Input, type InputProps } from "./input";

export type PasswordInputProps = Omit<InputProps, "type">;

/**
 * Password field with a reveal toggle. The toggle is `type="button"` so it can
 * never submit the form, and its accessible name states the resulting action.
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ className, ...props }, ref) {
    const [revealed, setRevealed] = React.useState(false);

    return (
      <span className="relative block">
        <Input
          ref={ref}
          type={revealed ? "text" : "password"}
          className={cn("pr-10", className)}
          {...props}
        />
        <button
          type="button"
          aria-label={revealed ? "Hide password" : "Show password"}
          aria-pressed={revealed}
          onClick={() => setRevealed((value) => !value)}
          className={cn(
            "text-muted-foreground hover:text-foreground focus-visible:ring-ring/50",
            "absolute right-0 top-0 flex h-9 w-10 items-center justify-center rounded-md",
            "focus-visible:outline-none focus-visible:ring-2",
          )}
        >
          {revealed ? (
            <EyeOff aria-hidden className="size-4" />
          ) : (
            <Eye aria-hidden className="size-4" />
          )}
        </button>
      </span>
    );
  },
);
PasswordInput.displayName = "PasswordInput";
